import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MAT_DATE_FORMATS, MatNativeDateModule } from '@angular/material/core';
import {
  MatCalendar,
  MatCalendarCellClassFunction,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { Subscription } from 'rxjs';

/** Selected ISO calendar dates (`YYYY-MM-DD`). */
export type DateRangeValue = string[];

const DATE_PICKER_DISPLAY_FORMATS = {
  parse: {
    dateInput: { day: '2-digit', month: 'short', year: 'numeric' },
  },
  display: {
    dateInput: { day: '2-digit', month: 'short', year: 'numeric' },
    monthYearLabel: { month: 'short', year: 'numeric' },
    dateA11yLabel: { day: '2-digit', month: 'long', year: 'numeric' },
    monthYearA11yLabel: { month: 'long', year: 'numeric' },
  },
};

@Component({
  selector: 'app-date-range-filter',
  standalone: true,
  imports: [
    CommonModule,
    MatDatepickerModule,
    MatIconModule,
    MatMenuModule,
    MatNativeDateModule,
  ],
  templateUrl: './date-range-filter.component.html',
  styleUrl: './date-range-filter.component.scss',
  encapsulation: ViewEncapsulation.None,
  providers: [{ provide: MAT_DATE_FORMATS, useValue: DATE_PICKER_DISPLAY_FORMATS }],
})
export class DateRangeFilterComponent implements OnDestroy {
  @ViewChild(MatCalendar) private calendar?: MatCalendar<Date>;

  private config: any = {};
  private readonly filterSubscription: Subscription;

  /**
   * JSON widget configuration. Supported values include `name`, `title`, `key`,
   * `placeholder`, `disabled`, `isQueryParam`, `filterListener`, `dropdownWidth`,
   * `dropdownHeight`, and `selectedDates`/`defaultValue`.
   */
  @Input()
  set data(value: any) {
    this.config = value || {};
    const currentFilters = this.globalFilterService.getCurrentFilters?.();
    const currentValue = this.data?.isQueryParam
      ? currentFilters?.queryParams?.[this.data?.key]
      : currentFilters?.requestParams?.[this.data?.key];
    const configuredDates = this.config?.selectedDates ?? this.config?.defaultValue;
    if (currentValue !== undefined) {
      this.selectedDates = this.normalizeDates(currentValue);
    } else if (configuredDates !== undefined) {
      this.selectedDates = this.normalizeDates(configuredDates);
      this.emitSelection();
    }
  }

  get data(): any {
    return this.config;
  }

  @Input() selectedDates: string[] = [];

  /** Kept for consumers of the former range component; now emits selected ISO dates. */
  @Output() rangeChange = new EventEmitter<DateRangeValue>();

  readonly dateClass: MatCalendarCellClassFunction<Date> = (date: Date) =>
    this.isSelected(date) ? 'multi-date-selected' : '';

  constructor(private readonly globalFilterService: GlobalFilterService) {
    this.filterSubscription = this.globalFilterService.filter$.subscribe((filters) => {
      const key = this.data?.key;
      if (!key) {
        return;
      }

      const value = this.data?.isQueryParam
        ? filters?.queryParams?.[key]
        : filters?.requestParams?.[key];
      this.selectedDates = this.normalizeDates(value);
    });
  }

  ngOnDestroy(): void {
    this.filterSubscription.unsubscribe();
  }

  get label(): string {
    return this.data?.name || this.data?.title || 'Date';
  }

  get placeholder(): string {
    return this.data?.placeholder || 'All';
  }

  get disabled(): boolean {
    return this.data?.disabled === true;
  }

  get controlStyles(): Record<string, string> {
    const width = this.normalizeCssSize(
      this.data?.dropdownWidth ?? this.data?.controlWidth ?? this.data?.width,
    );
    const height = this.normalizeCssSize(this.data?.dropdownHeight ?? this.data?.controlHeight);
    const styles: Record<string, string> = {};

    if (width) {
      styles['--date-filter-width'] = width;
    }
    if (height) {
      styles['--date-filter-height'] = height;
    }

    return styles;
  }

  toggleDate(date: Date | null): void {
    if (!date || this.disabled) {
      return;
    }

    const isoDate = this.toIsoDate(date);
    this.selectedDates = this.selectedDates.includes(isoDate)
      ? this.selectedDates.filter((selectedDate) => selectedDate !== isoDate)
      : [...this.selectedDates, isoDate].sort();
    this.refreshCalendarHighlights();
    this.emitSelection();
  }

  removeDate(date: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.disabled) {
      return;
    }

    this.selectedDates = this.selectedDates.filter((selectedDate) => selectedDate !== date);
    this.refreshCalendarHighlights();
    this.emitSelection();
  }

  clearDates(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.disabled || this.selectedDates.length === 0) {
      return;
    }

    this.selectedDates = [];
    this.refreshCalendarHighlights();
    this.emitSelection();
  }

  formatDate(isoDate: string): string {
    const date = this.fromIsoDate(isoDate);
    if (!date) {
      return isoDate;
    }

    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(date);
  }

  private isSelected(date: Date): boolean {
    return this.selectedDates.includes(this.toIsoDate(date));
  }

  private emitSelection(): void {
    const selectedDates = [...this.selectedDates];
    this.rangeChange.emit(selectedDates);

    if (!this.data?.key) {
      return;
    }

    const selectionTime = new Date();
    const payloadValue = selectedDates.map((date) =>
      this.toPayloadTimestamp(date, selectionTime),
    );

    this.globalFilterService.activeFilter = this.data.key;
    this.globalFilterService.updateFilterSelection(
      this.data.key,
      payloadValue,
      this.data?.isQueryParam || false,
      undefined,
      true,
      this.data?.filterListener || null,
    );
  }

  private refreshCalendarHighlights(): void {
    this.calendar?.updateTodaysDate();
  }

  private normalizeDates(value: unknown): string[] {
    const values = Array.isArray(value) ? value : value ? [value] : [];
    return [...new Set(values.map((date) => this.normalizeDate(date)).filter(Boolean))].sort();
  }

  private normalizeDate(value: unknown): string {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return this.toIsoDate(value);
    }

    if (typeof value !== 'string') {
      return '';
    }

    const isoMatch = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/);
    if (!isoMatch) {
      return '';
    }

    const date = this.fromIsoDate(`${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`);
    return date ? this.toIsoDate(date) : '';
  }

  private toPayloadTimestamp(isoDate: string, selectionTime: Date): string {
    const date = this.fromIsoDate(isoDate);
    if (!date) {
      return isoDate;
    }

    date.setHours(
      selectionTime.getHours(),
      selectionTime.getMinutes(),
      selectionTime.getSeconds(),
      selectionTime.getMilliseconds(),
    );

    const offsetMinutes = -date.getTimezoneOffset();
    const offsetSign = offsetMinutes >= 0 ? '+' : '-';
    const offsetHours = `${Math.floor(Math.abs(offsetMinutes) / 60)}`.padStart(2, '0');
    const offsetRemainder = `${Math.abs(offsetMinutes) % 60}`.padStart(2, '0');
    const hours = `${date.getHours()}`.padStart(2, '0');
    const minutes = `${date.getMinutes()}`.padStart(2, '0');
    const seconds = `${date.getSeconds()}`.padStart(2, '0');
    const microseconds = `${date.getMilliseconds()}`.padStart(3, '0') + '000';

    return `${isoDate}T${hours}:${minutes}:${seconds}.${microseconds}${offsetSign}${offsetHours}:${offsetRemainder}`;
  }

  private fromIsoDate(value: string): Date | null {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) {
      return null;
    }

    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return this.toIsoDate(date) === value ? date : null;
  }

  private toIsoDate(value: Date): string {
    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, '0');
    const day = `${value.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private normalizeCssSize(value: unknown): string {
    if (value === undefined || value === null || value === '') {
      return '';
    }

    return typeof value === 'number' ? `${value}px` : String(value).trim();
  }
}
